/**
 * Runtime server / URL derivation.
 *
 * The same build artifact is deployed to every server's subdomain
 * (eu.data-q.io, au.data-q.io, eu.sequence-research.com, ...), so all
 * URLs are derived from window.location at runtime instead of being
 * baked in at build time. Env vars are only used as a fallback in dev.
 */

/**
 * The known DATA(Q) servers — the single source of truth for the frontend.
 * Each is served from the subdomain of the same name (eu.data-q.io, ...).
 * Adding a server is a one-line change here; the value must match the
 * conductor's `servers` list and the org's `server_id` property in Kinde.
 * A server id need not be geographic — it could be a client-specific or
 * load-balancing server, e.g. "eu2" or "acme".
 */
export const SERVERS = ["eu", "au"] as const

export type ServerId = (typeof SERVERS)[number]

const isServer = (value: string): value is ServerId =>
  (SERVERS as readonly string[]).includes(value)

/**
 * Returns the server id of the current (or given) hostname, based on its
 * subdomain prefix: "eu.data-q.io" -> "eu". Hosts without a server prefix
 * (localhost, staging.data-q.io, the bare domain) return null, which means
 * server enforcement is skipped there.
 */
export const getServerFromHostname = (
  hostname: string = window.location.hostname
): ServerId | null => {
  const first = hostname.split(".")[0].toLowerCase()
  return isServer(first) ? first : null
}

/**
 * Strips the server prefix from the hostname:
 * "au.sequence-research.com" -> "sequence-research.com".
 * Hostnames without a server prefix are returned unchanged.
 */
export const getBaseDomain = (
  hostname: string = window.location.hostname
): string =>
  getServerFromHostname(hostname)
    ? hostname.split(".").slice(1).join(".")
    : hostname

/** The full origin of a server's deployment, e.g. "https://au.data-q.io" */
export const serverOrigin = (server: ServerId): string =>
  `${window.location.protocol}//${server}.${getBaseDomain()}`

/** The conductor API base of a server's deployment */
export const serverApiBase = (server: ServerId): string =>
  `${serverOrigin(server)}/api`

/**
 * The conductor API base for the current origin. In dev the API runs on
 * a different port, so the env var is used there.
 */
export const getApiBase = (): string =>
  import.meta.env.DEV
    ? (import.meta.env.VITE_API_ROUTE as string)
    : `${window.location.origin}/api`

/** Kinde OAuth callback for the current origin (must be registered in Kinde) */
export const getKindeRedirectUri = (): string =>
  `${window.location.origin}/home`

/** Kinde logout redirect for the current origin (must be registered in Kinde) */
export const getKindeLogoutUri = (): string => window.location.origin

/**
 * The collaboration WebSocket server for the current origin. Each server runs
 * its own collab server, so the host is derived at runtime (the same single
 * build serves every server). In dev the server runs on a separate port, so
 * the env var / localhost fallback is used there.
 */
export const getCollaborationWsUrl = (): string =>
  import.meta.env.DEV
    ? (import.meta.env.VITE_COLLABORATION_WS_URL as string) || "ws://localhost:1234"
    : `wss://${window.location.hostname}:1234`

/** All known servers except the given one. */
export const otherServers = (server: ServerId): ServerId[] =>
  SERVERS.filter((s) => s !== server)

/** A unit tagged with the server it lives on (null on non-server hosts) */
export interface ServerUnit {
  name: string
  serverId: ServerId | null
}
